import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRestaurantContext } from '@/contexts/RestaurantContext';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import { Header } from '@/components/layout/Header';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Receipt, X } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import type { AssistantPermissions } from '@/types/entities';

// Accounting components
import {
  PeriodSelector,
  type DateRange,
  type PeriodType,
} from '@/components/accounting/PeriodSelector';
import { FinancialOverview } from '@/components/accounting/FinancialOverview';
import { ExpensesTable } from '@/components/accounting/ExpensesTable';
import { AddExpenseDialog } from '@/components/accounting/AddExpenseDialog';
import { EditExpenseDialog } from '@/components/accounting/EditExpenseDialog';
import { EmployeesList } from '@/components/accounting/EmployeesList';
import { RecurringExpensesList } from '@/components/accounting/RecurringExpensesList';
import { CategoriesList } from '@/components/accounting/CategoriesList';
import { useExpenseCategories } from '@/hooks/useAccounting';
import { useExpenses, type ExpenseWithAudit } from '@/hooks/useExpenses';

import type { Expense } from '@/types/entities';

export function Accounting() {
  const { t, i18n } = useTranslation();
  const { selectedRestaurantId } = useRestaurantContext();
  const { data: assistant } = useCurrentAssistant();

  // Permission helper functions for assistants
  const canViewPage = (page: keyof AssistantPermissions): boolean => {
    if (!assistant?.permissions || !assistant.permissions[page]) return false;
    const pagePermissions = assistant.permissions[page] as Record<string, boolean>;
    return Object.values(pagePermissions).some((val) => val === true);
  };

  const hasPermission = (page: keyof AssistantPermissions, action: string): boolean => {
    if (!assistant?.permissions || !assistant.permissions[page]) return false;
    const pagePermissions = assistant.permissions[page] as Record<string, boolean>;
    return pagePermissions[action] === true;
  };

  // Check if user is an assistant
  const isAssistant = !!assistant;

  // For assistants, determine which tabs they can see
  const canViewSalaries = !isAssistant || canViewPage('salaries');
  const canViewExpensesPermission = !isAssistant || canViewPage('expenses');
  // Expenses tab visible if has expenses OR salaries permission (to see salary payments)
  const canViewExpensesTab = canViewExpensesPermission || canViewSalaries;
  // Categories tab is admin-only
  const canViewCategories = !isAssistant;

  // Permission checks for actions (admins always have permission)
  const canAddExpense = !isAssistant || hasPermission('expenses', 'add');

  // Determine expense filter mode for assistants
  // 'all' = show all expenses, 'salaries_only' = only salary payments, 'expenses_only' = only non-salary
  const expenseFilterMode = !isAssistant
    ? 'all'
    : canViewExpensesPermission && canViewSalaries
      ? 'all'
      : canViewSalaries
        ? 'salaries_only'
        : 'expenses_only';

  // Period state
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
  });

  // Dialog states
  const [showAddExpenseDialog, setShowAddExpenseDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);

  // Get expense categories for filtering
  const { data: categories } = useExpenseCategories(selectedRestaurantId);

  const handlePeriodChange = (range: DateRange, _periodType: PeriodType) => {
    setDateRange(range);
  };

  // Format dates for SQL queries
  const startDateStr = format(dateRange.startDate, 'yyyy-MM-dd');
  const endDateStr = format(dateRange.endDate, 'yyyy-MM-dd');

  // Get expenses with deleted for summary card (to show correct totals)
  const { data: expensesData, isLoading: expensesDataLoading } = useExpenses(selectedRestaurantId, {
    startDate: startDateStr,
    endDate: endDateStr,
    includeDeleted: true,
    limit: 1000,
  });

  // Calculate expense stats excluding deleted
  const expenseStats = {
    activeTotal: (expensesData?.expenses || [])
      .filter((e) => !(e as ExpenseWithAudit).deletedAt)
      .reduce((sum, e) => sum + e.amount, 0),
    activeCount: (expensesData?.expenses || []).filter((e) => !(e as ExpenseWithAudit).deletedAt)
      .length,
    deletedCount: (expensesData?.expenses || []).filter((e) => !!(e as ExpenseWithAudit).deletedAt)
      .length,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-MA' : 'fr-MA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Determine the default tab based on permissions
  const getDefaultTab = () => {
    if (canViewExpensesTab) return 'overview';
    if (canViewSalaries) return 'employees';
    return 'overview';
  };

  if (!selectedRestaurantId) {
    return (
      <ProtectedRoute requiredPermissions={['salaries', 'expenses']}>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Header />
          <main className="container mx-auto px-4 py-8">
            <div className="p-4 text-center text-muted-foreground">
              {t('restaurant.selectRestaurant')}
            </div>
          </main>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requiredPermissions={['salaries', 'expenses']}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{t('accounting.title')}</h1>
            <p className="text-muted-foreground">{t('accounting.description')}</p>
          </div>

          <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <PeriodSelector onPeriodChange={handlePeriodChange} defaultPeriod="this_month" />
            </div>

            {/* Main Tabs */}
            <Tabs defaultValue={getDefaultTab()} className="w-full">
              <TabsList className="mb-6 flex-wrap h-auto gap-1">
                {canViewExpensesTab && (
                  <TabsTrigger value="overview" className="data-[state=active]:bg-background">{t('accounting.tabs.overview')}</TabsTrigger>
                )}
                {canViewExpensesTab && (
                  <TabsTrigger value="expenses" className="data-[state=active]:bg-background">{t('accounting.tabs.expenses')}</TabsTrigger>
                )}
                {canViewSalaries && (
                  <TabsTrigger value="employees" className="data-[state=active]:bg-background">{t('accounting.tabs.employees')}</TabsTrigger>
                )}
                {canViewExpensesPermission && (
                  <TabsTrigger value="recurring" className="data-[state=active]:bg-background">{t('accounting.tabs.recurring')}</TabsTrigger>
                )}
                {canViewCategories && (
                  <TabsTrigger value="categories" className="data-[state=active]:bg-background">{t('accounting.tabs.categories')}</TabsTrigger>
                )}
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview">
                <FinancialOverview
                  restaurantId={selectedRestaurantId}
                  startDate={startDateStr}
                  endDate={endDateStr}
                  canViewSalaries={canViewSalaries}
                  canViewExpenses={canViewExpensesPermission}
                />
              </TabsContent>

              {/* Expenses Tab */}
              <TabsContent value="expenses">
                <div className="space-y-4">
                  {/* Expenses Overview */}
                  {expensesDataLoading ? (
                    <Card className="animate-pulse">
                      <CardHeader className="pb-2">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                      </CardHeader>
                      <CardContent>
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16" />
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          {t('accounting.expenses')}
                        </CardTitle>
                        <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900/30">
                          <Receipt className="h-4 w-4 text-purple-600" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-purple-600">
                          -{formatCurrency(Math.abs(expenseStats.activeTotal))}{' '}
                          {t('common.currency')}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {expenseStats.activeCount} {t('accounting.entries')}
                          {expenseStats.deletedCount > 0 && (
                            <span className="text-destructive ms-1">
                              ({expenseStats.deletedCount} {t('accounting.deletedEntries')})
                            </span>
                          )}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  <div className="flex justify-between items-center flex-wrap gap-4">
                    {/* Category Filters */}
                    {categories && categories.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {t('accounting.filterByCategory')}:
                        </span>
                        {categories
                          .filter((c) => c.isActive)
                          .map((category) => {
                            const isSelected = selectedCategoryId === category.id;
                            return (
                              <Badge
                                key={category.id}
                                variant={isSelected ? 'default' : 'outline'}
                                className="cursor-pointer hover:opacity-80 transition-opacity"
                                style={
                                  isSelected
                                    ? {
                                        backgroundColor: category.color,
                                        borderColor: category.color,
                                        color: 'white',
                                      }
                                    : {
                                        borderColor: category.color,
                                        color: category.color,
                                      }
                                }
                                onClick={() => {
                                  setSelectedCategoryId(isSelected ? undefined : category.id);
                                }}
                              >
                                {i18n.language === 'ar' ? category.nameAr : category.nameFr}
                                {isSelected && <X className="w-3 h-3 ms-1" />}
                              </Badge>
                            );
                          })}
                        {selectedCategoryId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedCategoryId(undefined)}
                            className="h-6 px-2 text-xs"
                          >
                            {t('common.clearFilters')}
                          </Button>
                        )}
                      </div>
                    )}
                    {canAddExpense && (
                      <Button onClick={() => setShowAddExpenseDialog(true)}>
                        <Plus className="w-4 h-4 me-2" />
                        {t('accounting.addExpense')}
                      </Button>
                    )}
                  </div>
                  <ExpensesTable
                    restaurantId={selectedRestaurantId}
                    startDate={startDateStr}
                    endDate={endDateStr}
                    {...(selectedCategoryId && { categoryId: selectedCategoryId })}
                    onEdit={(expense) => setEditingExpense(expense)}
                    filterMode={expenseFilterMode}
                  />
                </div>
              </TabsContent>

              {/* Employees Tab */}
              <TabsContent value="employees">
                <EmployeesList
                  restaurantId={selectedRestaurantId}
                  startDate={startDateStr}
                  endDate={endDateStr}
                />
              </TabsContent>

              {/* Recurring Expenses Tab */}
              <TabsContent value="recurring">
                <RecurringExpensesList
                  restaurantId={selectedRestaurantId}
                  startDate={startDateStr}
                  endDate={endDateStr}
                />
              </TabsContent>

              {/* Categories Tab */}
              <TabsContent value="categories">
                <CategoriesList restaurantId={selectedRestaurantId} />
              </TabsContent>
            </Tabs>
          </div>
        </main>

        {/* Add Expense Dialog */}
        <AddExpenseDialog
          restaurantId={selectedRestaurantId}
          open={showAddExpenseDialog}
          onOpenChange={setShowAddExpenseDialog}
        />

        {/* Edit Expense Dialog */}
        {editingExpense && (
          <EditExpenseDialog
            restaurantId={selectedRestaurantId}
            expense={editingExpense}
            open={!!editingExpense}
            onOpenChange={(open) => !open && setEditingExpense(null)}
          />
        )}
      </div>
    </ProtectedRoute>
  );
}
